<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useVideoWatchdog } from '../composables/useVideoWatchdog.js';

const props = defineProps({
  content: { type: Object, required: true },
  boxStyle: { type: String, required: false, default: '' },
});

const emit = defineEmits(['takeOverTimer', 'next']);
const { ping: watchdogPing, stop: watchdogStop } = useVideoWatchdog(emit);

const videoId = computed(() => {
  return props.content.video_id;
});

// TikTok autoplay-block error code. When autoplay-with-sound is denied,
// the player emits onPlayerError with this code; we recover by muting
// and re-issuing play.
const TIKTOK_AUTOPLAY_ERROR_CODE = 3002;

const videoUrl = computed(() => {
  const params = [ 'autoplay=1', 'loop=0', 'controls=0' ];
  if (!props.content.audio) {
    params.push('muted=1');
  }
  return `https://www.tiktok.com/player/v1/${videoId.value}?${params.join('&')}`;
});

const playerRef = ref(null);

const hasDuration = computed(() => {
  return props.content.duration && props.content.duration > 0;
});

function sendCommand(type) {
  const iframe = playerRef.value;
  if (!iframe || !iframe.contentWindow) return;
  iframe.contentWindow.postMessage({
    'x-tiktok-player': true,
    type,
    value: null
  }, 'https://www.tiktok.com');
}

function fallbackToMuted() {
  console.warn('TikTok autoplay-with-sound blocked, falling back to muted');
  sendCommand('mute');
  sendCommand('play');
}

function handlePlayerMessage(event) {
  // Validate message origin for security
  if (event.origin !== 'https://www.tiktok.com') {
    return;
  }

  // Only process messages from TikTok player
  if (!event.data || !event.data['x-tiktok-player']) {
    return;
  }

  const { type, value } = event.data;

  switch (type) {
  case 'onStateChange':
    // State values: -1 (init), 0 (ended), 1 (playing), 2 (paused), 3 (buffering)
    switch (value) {
    case 1: // playing
      console.debug('TikTok video is playing');
      watchdogPing();
      if (!hasDuration.value) {
        emit('takeOverTimer', {});
      } else {
        console.debug('TikTok video has a duration, not taking over timer');
      }
      break;
    case 2: // paused
      console.debug('TikTok video is paused');
      break;
    case 0: // ended
      console.debug('TikTok video ended');
      watchdogStop();
      if (!hasDuration.value) {
        emit('next', {});
      }
      break;
    }
    break;
  case 'onCurrentTime':
    watchdogPing();
    break;
  case 'onPlayerReady':
    console.debug('TikTok player is ready');
    break;
  case 'onPlayerError': {
    console.error('TikTok player error:', value);
    const code = typeof value === 'object' && value !== null ? value.code : value;
    if (props.content.audio && code === TIKTOK_AUTOPLAY_ERROR_CODE) {
      fallbackToMuted();
    }
    break;
  }
  }
}

onMounted(() => {
  window.addEventListener('message', handlePlayerMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handlePlayerMessage);
});
</script>

<template>
  <div
    class="video-container"
    :style="boxStyle"
  >
    <iframe
      ref="playerRef"
      class="player"
      type="text/html"
      frameborder="0"
      allow="autoplay; encrypted-media"
      :src="videoUrl"
      :style="{ aspectRatio: content.aspect_ratio }"
    />
  </div>
</template>

<style scoped>
.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.player {
  max-width: 100%;
  max-height: 100%;
}
</style>
